import { inject } from '@angular/core';
import type { ResolveFn } from '@angular/router';
import { withVisit } from '../domain/member-preferences/comparison-shortcuts-prefs';
import { MemberPreferencesStore } from '../domain/member-preferences/member-preferences-store';
import { OrganizationStore } from '../domain/organization/organization-store';
import { SegmentStore } from '../domain/segment/segment-store';
import { parseSegmentKey } from '../domain/segment/segment-url';

/**
 * Records a visit to /portal/compare/<pbKey> as the member's most recent comparison (AC-17,
 * D-10). A visit is this navigation, so it is recorded once, here, and never re-derived from
 * whatever the stores hold later.
 *
 * It never holds the route up: it returns at once and records when the comparison list can
 * vouch for the key. Only an authored comparison (`SegmentStore.authoredSegments`) is recorded,
 * so a curated one never takes one of the five slots. With no preferences row loaded, or no
 * comparison list to vouch for the key, the visit is dropped.
 */
export const comparisonVisitResolver: ResolveFn<void> = (route) => {
  const parsed = parseSegmentKey(route.paramMap.get('pbKey') ?? '');
  if (!parsed) return;
  const segmentStore = inject(SegmentStore);
  const orgStore = inject(OrganizationStore);
  const prefs = inject(MemberPreferencesStore);
  void recordVisit(parsed.id6, { segmentStore, orgStore, prefs });
};

type VisitDeps = {
  readonly segmentStore: Pick<SegmentStore, 'settled' | 'byUrlSuffix' | 'authoredSegments'>;
  /** Visibility is decided per org, so the list vouches for a key only once the org is known. */
  readonly orgStore: Pick<OrganizationStore, 'settled'>;
  readonly prefs: Pick<
    MemberPreferencesStore,
    'loaded' | 'recentComparisons' | 'setRecentComparisons'
  >;
};

async function recordVisit(id6: string, deps: VisitDeps): Promise<void> {
  try {
    await Promise.all([deps.segmentStore.settled(), deps.orgStore.settled()]);
  } catch (e) {
    console.error('comparisonVisitResolver: the comparison list failed to load, visit dropped', e);
    return;
  }
  const saved = deps.segmentStore.authoredSegments();
  const visited = deps.segmentStore.byUrlSuffix(id6);
  if (!visited || !saved.some((s) => s.id === visited.id)) return;
  if (!deps.prefs.loaded()) {
    console.warn('comparisonVisitResolver: no preferences row loaded, visit dropped');
    return;
  }
  deps.prefs.setRecentComparisons(withVisit(deps.prefs.recentComparisons(), visited.id));
}
